import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, Link, useLocation } from 'react-router-dom'
import { useTenant } from '../../tenant/useTenant'
import { useAppSelector } from '../../store/hooks'
import { useDialog } from '../../hooks/useDialog'
import { cn } from '../../utils/cn'
import { DAY_NAMES, formatBusinessHour, weekOrdered } from '../../utils/hours'
import { Button, IconButton } from '../ui'

interface NavItem {
  to: string
  label: string
}

export function AppShell() {
  const { slug, bootstrap } = useTenant()
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  const { vendor, theme, branches } = bootstrap
  const nav: NavItem[] = [
    { to: `/${slug}/services`, label: 'Services' },
    ...(branches.length > 1 ? [{ to: `/${slug}/branches`, label: 'Locations' }] : []),
  ]

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-50 w-full border-b border-primary/10 bg-surface/80 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-container-max items-center justify-between px-margin-mobile py-4 md:px-gutter">
          <Brand slug={slug} name={vendor.name} logo={theme.logo} />

          <nav aria-label="Main navigation" className="hidden items-center gap-8 md:flex">
            {nav.map((item) => (
              <NavLink key={item.to} to={item.to} className={navLinkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-4">
            <Link
              to={isAuthenticated ? `/${slug}/account` : `/${slug}/signin`}
              aria-label={isAuthenticated ? 'My account' : 'Sign in'}
              className="hidden h-10 w-10 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-primary/5 hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary md:inline-flex"
            >
              <span className={cn('material-symbols-outlined', isAuthenticated && 'filled')}>person</span>
            </Link>

            <Button to={`/${slug}/book`} size="lg" className="hidden sm:inline-flex">
              Book now
            </Button>

            <IconButton icon="menu" label="Open menu" className="md:hidden" onClick={() => setDrawerOpen(true)} />
          </div>
        </div>
      </header>

      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slug={slug}
        name={vendor.name}
        nav={nav}
        isAuthenticated={isAuthenticated}
      />

      <main className="flex-1">
        <Outlet />
      </main>

      <SiteFooter slug={slug} nav={nav} />
    </div>
  )
}

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'px-3 py-2 font-label-caps text-label-caps uppercase transition-colors duration-300',
    isActive
      ? 'border-b-2 border-primary pb-1 font-bold text-primary'
      : 'rounded text-on-surface-variant hover:bg-primary/5 hover:text-primary',
  )
}

function Brand({ slug, name, logo }: { slug: string; name: string; logo: string | null }) {
  return (
    <Link to={`/${slug}`} className="flex min-w-0 items-center gap-3">
      {logo ? (
        <img src={logo} alt={name} className="h-9 w-auto object-contain" />
      ) : (
        <span className="truncate font-display text-headline-sm tracking-tighter text-primary">{name}</span>
      )}
    </Link>
  )
}

function drawerLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'rounded-lg px-4 py-4 font-label-caps text-label-caps uppercase transition-colors',
    isActive ? 'bg-primary-fixed text-on-primary-fixed-variant' : 'text-on-surface hover:bg-primary/5',
  )
}

function MobileDrawer({
  open,
  onClose,
  slug,
  name,
  nav,
  isAuthenticated,
}: {
  open: boolean
  onClose: () => void
  slug: string
  name: string
  nav: NavItem[]
  isAuthenticated: boolean
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  useDialog(open, onClose, panelRef)

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[90] md:hidden">
      <div aria-hidden="true" onClick={onClose} className="absolute inset-0 bg-inverse-surface/40 backdrop-blur-sm" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${name} menu`}
        tabIndex={-1}
        className="absolute inset-y-0 right-0 flex w-[min(20rem,85%)] flex-col border-l border-primary/10 bg-surface outline-none"
      >
        <div className="flex items-center justify-between border-b border-primary/10 px-margin-mobile py-4">
          <span className="font-display text-headline-sm text-primary">{name}</span>
          <IconButton icon="close" label="Close menu" onClick={onClose} />
        </div>

        <nav aria-label="Mobile navigation" className="flex flex-1 flex-col gap-1 overflow-y-auto p-margin-mobile">
          {nav.map((item) => (
            <NavLink key={item.to} to={item.to} className={drawerLinkClass}>
              {item.label}
            </NavLink>
          ))}
          <NavLink to={isAuthenticated ? `/${slug}/account` : `/${slug}/signin`} className={drawerLinkClass}>
            {isAuthenticated ? 'My account' : 'Sign in'}
          </NavLink>
        </nav>

        <div className="border-t border-primary/10 p-margin-mobile">
          <Button to={`/${slug}/book`} size="lg" fullWidth>
            Book now
          </Button>
        </div>
      </div>
    </div>
  )
}

function SiteFooter({ slug, nav }: { slug: string; nav: NavItem[] }) {
  const { bootstrap } = useTenant()
  const { vendor, branches } = bootstrap
  // One salon, one set of hours in the footer; multi-branch vendors get the
  // primary branch here and the full list on /branches.
  const primary = branches[0]
  const hours = primary ? weekOrdered(primary.business_hours) : []
  const socials = Object.entries(vendor.social).filter((entry): entry is [string, string] => Boolean(entry[1]))

  return (
    <footer className="mt-section-gap-lg bg-primary text-on-primary">
      <div className="mx-auto grid w-full max-w-container-max grid-cols-1 gap-12 px-margin-mobile py-section-gap-md md:grid-cols-4 md:gap-gutter md:px-gutter">
        <div className="md:col-span-1">
          <Link to={`/${slug}`} className="mb-4 block font-display text-headline-sm">
            {vendor.name}
          </Link>
          {vendor.tagline && <p className="max-w-xs font-body-md text-body-md opacity-80">{vendor.tagline}</p>}
        </div>

        <div className="flex flex-col gap-2">
          <span className="mb-2 font-label-caps text-label-caps uppercase opacity-50">Explore</span>
          {nav.map((item) => (
            <Link key={item.to} to={item.to} className="font-body-md text-body-md opacity-80 hover:opacity-100">
              {item.label}
            </Link>
          ))}
          <Link to={`/${slug}/book`} className="font-body-md text-body-md opacity-80 hover:opacity-100">
            Book an appointment
          </Link>
        </div>

        <div className="flex flex-col gap-2">
          <span className="mb-2 font-label-caps text-label-caps uppercase opacity-50">Visit us</span>
          {primary && (
            <p className="font-body-md text-body-md opacity-80">
              {[primary.address, primary.city].filter(Boolean).join(', ')}
            </p>
          )}
          {vendor.phone && (
            <a href={`tel:${vendor.phone}`} className="font-body-md text-body-md opacity-80 hover:opacity-100">
              {vendor.phone}
            </a>
          )}
          {vendor.email && (
            <a href={`mailto:${vendor.email}`} className="font-body-md text-body-md opacity-80 hover:opacity-100">
              {vendor.email}
            </a>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <span className="mb-2 font-label-caps text-label-caps uppercase opacity-50">Hours</span>
          {hours.length ? (
            <ul className="font-body-md text-body-md opacity-80">
              {hours.map((hour) => (
                <li key={hour.day} className="flex justify-between gap-4 md:max-w-[14rem]">
                  <span>{DAY_NAMES[hour.day]}</span>
                  <span>{formatBusinessHour(hour)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="font-body-md text-body-md opacity-80">Call us for current availability.</p>
          )}
        </div>
      </div>

      <div className="mx-auto w-full max-w-container-max border-t border-on-primary/20 px-margin-mobile py-6 md:px-gutter">
        {/* The admin field is labelled "Footer HTML/text", but it is rendered as
            text on purpose: it is vendor-supplied content on a multi-tenant
            domain, so injecting it as markup would hand any salon a script tag
            on every other salon's storefront. */}
        {vendor.footer_text && (
          <p className="mb-4 max-w-2xl whitespace-pre-line font-body-md text-sm opacity-70">{vendor.footer_text}</p>
        )}

        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <p className="font-body-md text-sm opacity-60">
            {vendor.copyright_text || `© ${new Date().getFullYear()} ${vendor.name}`}
          </p>
          {socials.length > 0 && (
            <ul className="flex flex-wrap gap-4">
              {socials.map(([platform, url]) => (
                <li key={platform}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 font-label-caps text-label-caps uppercase opacity-80 hover:opacity-100"
                  >
                    {platform}
                    <span className="material-symbols-outlined text-[14px]">arrow_outward</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </footer>
  )
}
